import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useResolve } from '@/infrastructure/useResolve';
import { TOKEN_ClientsStore } from './ClientsStore';
import type { ClientSpec } from '@/domain/admin';

// Local, ephemeral form state (the mvvm-view carve-out, like CopyButton). The store owns the durable data.
function lines(v: string): string[] {
  return v
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

export const ClientsView = observer(function ClientsView() {
  const store = useResolve(TOKEN_ClientsStore);
  const [newId, setNewId] = useState('');
  const [redirects, setRedirects] = useState('');
  const [scopes, setScopes] = useState('openid');
  const [isPublic, setIsPublic] = useState(true);

  useEffect(() => {
    void store.load();
  }, [store]);

  const spec = (): ClientSpec => ({ redirect_uris: lines(redirects), scopes: lines(scopes.replace(/ /g, '\n')), post_logout_redirect_uris: [] });

  return (
    <div className="grid gap-6 md:grid-cols-[18rem_1fr]" data-testid="clients-screen">
      <section className="rounded-lg border border-slate-200 dark:border-slate-800">
        <header className="border-b border-slate-200 px-3 py-2 text-sm font-medium dark:border-slate-800">Clients</header>
        <ul className="max-h-[20rem] overflow-auto" data-testid="client-list">
          {store.clients.map((c) => (
            <li key={c.client_id}>
              <button
                type="button"
                onClick={() => store.select(c.client_id)}
                data-testid={`client-${c.client_id}`}
                className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800 ${
                  store.selectedClientId === c.client_id ? 'bg-indigo-50 dark:bg-slate-800' : ''
                }`}
              >
                <span className="font-mono text-xs">{c.client_id}</span>
                <span className="text-xs text-slate-400">{c.public ? 'public' : 'confidential'}</span>
              </button>
            </li>
          ))}
        </ul>
        <form
          className="space-y-2 border-t border-slate-200 p-3 dark:border-slate-800"
          onSubmit={(e) => {
            e.preventDefault();
            void store.create(newId, spec(), isPublic).then(() => setNewId(''));
          }}
        >
          <p className="text-xs font-medium uppercase text-slate-400">New client</p>
          <input value={newId} onChange={(e) => setNewId(e.target.value)} placeholder="client_id" data-testid="new-client-id" className="w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900" />
          <textarea value={redirects} onChange={(e) => setRedirects(e.target.value)} placeholder="redirect URIs (one per line)" rows={2} className="w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900" />
          <input value={scopes} onChange={(e) => setScopes(e.target.value)} placeholder="scopes (space-separated)" className="w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} data-testid="new-client-public" /> public (PKCE, no secret)
          </label>
          <button type="submit" disabled={store.busy} data-testid="create-client" className="w-full rounded bg-indigo-600 px-3 py-1 text-sm text-white disabled:opacity-50">
            create
          </button>
        </form>
      </section>

      <section className="rounded-lg border border-slate-200 p-4 dark:border-slate-800" data-testid="client-detail">
        {store.revealedSecret && (
          <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm dark:bg-amber-950/30" data-testid="revealed-secret">
            <p className="font-medium text-amber-800 dark:text-amber-300">Secret for {store.revealedSecret.clientId}, copy it now, it is shown only once:</p>
            <code className="mt-1 block break-all font-mono text-xs">{store.revealedSecret.secret}</code>
            <button type="button" onClick={() => store.dismissSecret()} className="mt-2 text-xs text-amber-700 hover:underline">
              dismiss
            </button>
          </div>
        )}
        {!store.selected && <p className="text-sm text-slate-500">Select or create a client.</p>}
        {store.selected && (
          <div className="space-y-3">
            <h3 className="font-mono text-lg">{store.selected.client_id}</h3>
            <p className="text-sm text-slate-500">{store.selected.public ? 'public' : 'confidential'}</p>
            <div className="text-sm">
              <p className="text-xs font-medium uppercase text-slate-400">Redirect URIs</p>
              <ul className="font-mono text-xs">{store.selected.redirect_uris.map((u) => <li key={u}>{u}</li>)}</ul>
              <p className="mt-2 text-xs font-medium uppercase text-slate-400">Scopes</p>
              <p className="text-xs">{store.selected.scopes.join(' ') || '-'}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void store.update({ redirect_uris: store.selected!.redirect_uris, scopes: store.selected!.scopes, post_logout_redirect_uris: store.selected!.post_logout_redirect_uris })}
                disabled={store.busy}
                title="re-save current values (edit redirect URIs in the create panel for now)"
                className="rounded border border-slate-300 px-3 py-1 text-sm dark:border-slate-700"
              >
                save
              </button>
              <button type="button" onClick={() => void store.rotateSecret()} disabled={store.busy} data-testid="rotate-secret" className="rounded border border-slate-300 px-3 py-1 text-sm dark:border-slate-700">
                rotate secret
              </button>
              <button type="button" onClick={() => void store.remove()} disabled={store.busy} data-testid="delete-client" className="rounded border border-rose-300 px-3 py-1 text-sm text-rose-700">
                delete
              </button>
            </div>
          </div>
        )}
        {store.notice && <p className="mt-3 text-sm text-emerald-600" data-testid="client-notice">{store.notice}</p>}
        {store.error && <p className="mt-3 text-sm text-rose-600" data-testid="client-error">{store.error}</p>}
      </section>
    </div>
  );
});
